<script setup>
/* Imports */
import {
  ref,
  computed,
  onBeforeMount,
  useTemplateRef,
  nextTick,
  watch,
  Teleport,
} from 'vue';
import FuzzySearch from '@garmin/fuzzy-search';
import AutoComplete from './AutoComplete.vue';
import { useAutocompleteStore } from '../store/autocomplete.js';
import { useGlobalStore } from '../store/global.js';
import { usePricingStore } from '../store/pricing.js';

/* Store */
const autocompleteStore = useAutocompleteStore();
const globalStore = useGlobalStore();
const pricingStore = usePricingStore();

/* Props */
const props = defineProps({
  stickyMenuActive: {
    type: Boolean,
    default: false,
  },
  maxProducts: {
    type: Number,
    default: 2,
  },
});

/* State */
const state = ref({
  inputValue: '', // input field binding
  showSearch: false,
});

/* Ref */
const templateRefs = {
  autocomplete: useTemplateRef('autocomplete'),
};

/* Computed settings for the autocomplete */
const autocompleteSettings = computed(() => ({
  list: autocompleteStore.getAutocompleteProductList,
  minChars: 1,
  maxItems: 6,
  filter(text, input) {
    return FuzzySearch.isFuzzyMatch(text, input);
  },
  sort(previousProduct, nextProduct) {
    return (nextProduct?.value?.productId || 0) - (previousProduct?.value?.productId || 0);
  },
  item(text, input, itemId) {
    const regExpEscape = (s) => s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
    const html = input.trim() === ''
      ? text
      : text.replace(RegExp(regExpEscape(input.trim()), 'gi'), '<mark>$&</mark>');
    const element = document.createElement('li');
    element.setAttribute('id', `awesomplete-list-item-${itemId}`);
    element.setAttribute('data-testid', `awesomplete-list-item-${itemId}`);
    element.setAttribute('aria-selected', 'false');
    element.setAttribute('role', 'option');
    element.innerHTML = html;
    return element;
  },
  replace(suggestion) {
    state.value.inputValue = suggestion.label;
  },
  input: state.value.inputValue,
}));

/* Lifecycle Hooks */
onBeforeMount(async () => {
  await autocompleteStore.loadAutocompleteProductList();
});

/* Autocomplete select */
const onAutoCompleteSelectComplete = async (eventData) => {
  const pid = eventData?.productId;

  // Early return if no pid
  if (!pid) return;

  await globalStore.addProduct(pid);
  await pricingStore.fetchPriceForProduct(pid);

  state.value.showSearch = false;
};

/* Computed Teleport */
const computedTeleport = computed(() => {
  const isMobile = props.maxProducts <= 2;
  const shouldTeleport = props.stickyMenuActive && !isMobile;

  return {
    is: shouldTeleport ? Teleport : 'div',
    to: shouldTeleport ? '#sticky-search-target' : undefined,
  };
});

/* Autocomplete Focus */
const autocompleteFocus = () => {
  templateRefs.autocomplete.value?.focusInput?.();
};

/* Click on the card */
const onCardClick = async () => {
  state.value.showSearch = true;
  await nextTick();
  autocompleteFocus();
};

/* Watch for sticky menu re-focus */
watch(() => props.stickyMenuActive, async (newValue, oldValue) => {
  if (newValue !== oldValue && state.value.showSearch) {
    // If the input had focus, transfer the focus to the new input
    const hadFocus = document.activeElement?.id === 'product-search-input';

    if (hadFocus) {
      await nextTick();
      autocompleteFocus();
    }
  }
});
</script>

<template>
  <div
    class="card add-product-to-compare-card"
    data-testid="add-product-to-compare-card"
  >
    <div
      class="add-product-to-compare-card__header"
      data-testid="add-product-to-compare-card-header"
      tabindex="0"
      @click="onCardClick"
      @keydown.enter="onCardClick"
      @keydown.space="onCardClick"
    >
      <div class="add-product-to-compare-card__content">
        <div class="add-product-to-compare-card__icon-wrapper">
          <g-icon-add-alt
            data-testid="add-product-to-compare-card-add-icon"
          />
        </div>
      </div>
    </div>

    <div
      class="add-product-to-compare-card__body"
      data-testid="add-product-to-compare-card-body"
    >
      <div class="add-product-to-compare-card__inner">
        <div
          v-if="state.showSearch"
          class="add-product-to-compare-card__autocomplete"
          data-testid="add-product-to-compare-card-autocomplete"
        >
          <component
            :is="computedTeleport.is"
            :to="computedTeleport.to"
          >
            <AutoComplete
              id="product-search-input"
              key="product-search-input"
              ref="autocomplete"
              v-model="state.inputValue"
              :label="autocompleteStore.getProductAutocompleteText"
              :placeholder="autocompleteStore.getProductAutocompleteText"
              :settings="autocompleteSettings"
              :disabled="autocompleteStore.getIsAutocompleteDisabled"
              @selectcomplete="onAutoCompleteSelectComplete"
            />
          </component>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.add-product-to-compare-card {
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  position: relative;

  &__header {
    position: relative;
    cursor: pointer;
  }

  &__content {
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 1.125rem 0.75rem;
    @include make-drop-shadow();
  }

  &__icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 7.8125rem;   // 125px
    height: 7.8125rem;  // 125px

    @include breakpoint('sm') {
      width: 9.375rem;  // 150px
      height: 9.375rem; // 150px
    }

    :deep() g-icon-add-alt {
      display: flex;
      justify-content: center;
      width: 4rem; // 64px
      height: 4rem; // 64px
    }
  }

  &__body {
    // Mobile: sticky docked bar
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    padding: 1rem;
    box-sizing: border-box;
    background: $color-gray-95;
    border-top: 1px solid $color-gray-85;
    z-index: 2;

    @include breakpoint('sm') {
      padding: 1rem 0 0 0;
      display: flex;
      flex: 1 1;
      flex-direction: column;
      background: none;
      position: relative;
      border-top: none;
    }
  }

  &__inner {
    flex-grow: 1;
    width: 100%;
  }

  &__autocomplete {
    width: 100%;
  }
}

/* Sticky menu */
.sticky-menu.active .add-product-to-compare-card {

  &__body {
    padding: 1rem;

    @include breakpoint('sm') {
      padding: 0;
    }
  }

  &__icon-wrapper {
    width: 3.75rem; // 60px
    height: 3.75rem; // 60px

    :deep() g-icon-add-alt {
      width: 3rem; // 48px
    }
  }
}
</style>
